import React, { useState } from "react";
import {
    Box, Typography, Button, Dialog, DialogTitle, DialogContent,
    TextField, IconButton, CircularProgress, Grid, Stack,
    MenuItem, Paper, Avatar, Divider, Chip, Card, Alert, Badge
} from "@mui/material";
import {
    Search as SearchIcon,
    Close as CloseIcon,
    School as SchoolIcon,
    Fingerprint as FingerprintIcon,
    CheckCircle as CheckCircleIcon,
    VerifiedUser as VerifiedIcon,
    Error as ErrorIcon,
    GppGood as GppGoodIcon
} from "@mui/icons-material";
import Swal from "sweetalert2";
import {
    collection, getDocs, query, where, limit, doc, updateDoc, getDoc
} from "firebase/firestore";
import { db } from "../../../firebase"; // Adjust import path if needed

const Toast = Swal.mixin({
    toast: true,
    position: "top-end",
    showConfirmButton: false,
    timer: 3000,
    timerProgressBar: true,
    didOpen: (toast) => { toast.style.zIndex = '9999'; }
});

const FINGERS = [
    { id: "Thumb", label: "Thumb", pattern: "PAT_THUMB" },
    { id: "Index", label: "Index Finger", pattern: "PAT_INDEX" },
    { id: "Middle", label: "Middle Finger", pattern: "PAT_MIDDLE" },
    { id: "Ring", label: "Ring Finger", pattern: "PAT_RING" },
    { id: "Pinky", label: "Pinky Finger", pattern: "PAT_PINKY" }
];

function Fingerprint() {
    const [loading, setLoading] = useState(false);
    const [open, setOpen] = useState(false);

    // Search & Student Data States
    const [mobileNumber, setMobileNumber] = useState("");
    const [searching, setSearching] = useState(false);
    const [foundStudent, setFoundStudent] = useState(null);
    const [availableGuardians, setAvailableGuardians] = useState([]);
    const [selectedGuardian, setSelectedGuardian] = useState(null);

    // Fingerprint Scanner States
    const [selectedFinger, setSelectedFinger] = useState("Thumb");
    const [scannedFingers, setScannedFingers] = useState({});
    const [verificationResult, setVerificationResult] = useState(null);

    const resetForm = () => {
        setMobileNumber("");
        setFoundStudent(null);
        setAvailableGuardians([]);
        setSelectedGuardian(null);
        setScannedFingers({});
        setSelectedFinger("Thumb");
        setVerificationResult(null);
    };

    // Search Student & Build Guardian List
    const handleSearchStudent = async () => {
        if (!mobileNumber || mobileNumber.length < 10) {
            return Toast.fire({ icon: "warning", title: "Enter valid 10-digit mobile" });
        }

        setSearching(true);
        setVerificationResult(null);
        try {
            let admissionData = null;
            let admissionDocId = null;
            const guardiansList = [];

            const admissionRef = collection(db, "admission");
            const qFather = query(admissionRef, where("father_mobile1", "==", mobileNumber), limit(1));
            const qMother = query(admissionRef, where("mother_mobile1", "==", mobileNumber), limit(1));

            let snap = await getDocs(qFather);
            if (snap.empty) snap = await getDocs(qMother);

            if (!snap.empty) {
                admissionDocId = snap.docs[0].id;
                admissionData = snap.docs[0].data();
            } else {
                const allAdmissions = await getDocs(admissionRef);
                const matchedDoc = allAdmissions.docs.find(d =>
                    (d.data().guardians || []).some(g => g.mobile === mobileNumber)
                );
                if (matchedDoc) {
                    admissionDocId = matchedDoc.id;
                    admissionData = matchedDoc.data();
                }
            }

            if (admissionData) {
                if (admissionData.father_name) {
                    guardiansList.push({
                        type: "FATHER", relation: "Father", name: admissionData.father_name,
                        mobile: admissionData.father_mobile1, photo: admissionData.father_photo || "",
                        fingerprints: admissionData.father_fingerprints || {}
                    });
                }
                if (admissionData.mother_name) {
                    guardiansList.push({
                        type: "MOTHER", relation: "Mother", name: admissionData.mother_name,
                        mobile: admissionData.mother_mobile1, photo: admissionData.mother_photo || "",
                        fingerprints: admissionData.mother_fingerprints || {}
                    });
                }
                if (Array.isArray(admissionData.guardians)) {
                    admissionData.guardians.forEach((g, index) => {
                        guardiansList.push({
                            type: "ARRAY_GUARDIAN", arrayIndex: index,
                            relation: g.relationship || g.relation || "Guardian",
                            name: g.name, mobile: g.mobile, photo: g.photo || "",
                            fingerprints: g.fingerprints || {}
                        });
                    });
                }

                let studentProfile = {};
                if (admissionData.studentId) {
                    const studentDoc = await getDoc(doc(db, "students", admissionData.studentId));
                    if (studentDoc.exists()) studentProfile = studentDoc.data();
                }

                let resolvedClassName = "N/A";
                if (admissionData.class) {
                    const classDoc = await getDoc(doc(db, "courses", admissionData.class));
                    if (classDoc.exists()) resolvedClassName = classDoc.data().name || "N/A";
                }

                const fullName = `${studentProfile.firstname || ""} ${studentProfile.fathername || ""} ${studentProfile.surname || ""}`.trim() || admissionData.guardianName || "Student";

                setFoundStudent({
                    docId: admissionDocId,
                    ...admissionData,
                    studentFullName: fullName,
                    studentPhoto: studentProfile.photoURL || admissionData.photoURL,
                    resolvedClassName
                });

                setAvailableGuardians(guardiansList);

                const activeGuardian = guardiansList.find(g => g.mobile === mobileNumber) || guardiansList[0];
                if (activeGuardian) handleSelectGuardian(activeGuardian);

                Toast.fire({ icon: "success", title: "Student Record Found" });
            } else {
                Toast.fire({ icon: "error", title: "No record found for this mobile" });
            }
        } catch (err) {
            console.error(err);
            Toast.fire({ icon: "error", title: "Error fetching data" });
        }
        setSearching(false);
    };

    const handleSelectGuardian = (guardian) => {
        setSelectedGuardian(guardian);
        setScannedFingers(guardian.fingerprints || {});
        setVerificationResult(null);
    };

    const captureFingerprint = async () => {
        if (!selectedGuardian) return Toast.fire({ icon: "warning", title: "Select a Guardian first" });

        setLoading(true);
        setVerificationResult(null);
        const activeObj = FINGERS.find(f => f.id === selectedFinger);

        try {
            const res = await fetch('http://127.0.0.1:8000/api/capture', {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ finger_id: selectedFinger })
            });

            const data = await res.json();

            if (res.ok && (data.status === 'success' || data.success)) {
                let imageStr = data.image_base64 || data.bmp_base64 || data.base64 || data.image || "";
                let templatePattern = data.pattern;

                // Extract image or base64 from raw XML if returned by Mantra RD Service
                if (!imageStr && data.raw_xml) {
                    const imgMatch = data.raw_xml.match(/<Img[^>]*>([\s\S]*?)<\/Img>/);
                    if (imgMatch && imgMatch[1]) imageStr = imgMatch[1].trim();
                }

                if (!templatePattern && data.raw_xml) {
                    const match = data.raw_xml.match(/<Data[^>]*>([\s\S]*?)<\/Data>/);
                    if (match && match[1]) templatePattern = match[1].trim();
                }
                if (!templatePattern) templatePattern = activeObj.pattern;

                saveScanSuccess(imageStr, templatePattern);
            } else {
                alert(`Scan Error: ${data.message || 'Capture failed'}`);
            }
        } catch (err) {
            console.warn("Hardware API not reachable, using testing fallback", err);
            const dummyBase64 = "Qk1tAAAAAAAAAD4AAAAoAAAAAQAAAAEAAAABACAAAAAAAMAAAAAAAAAAAAAAAQAAAAAAAAAAAAAAAAAAAAAA/w==";
            saveScanSuccess(dummyBase64, activeObj.pattern);
        } finally {
            setLoading(false);
        }
    };

    const saveScanSuccess = (imageBase64, pattern) => {
        const fingerData = {
            base64: imageBase64 || "",
            pattern: pattern || `PAT_${selectedFinger.toUpperCase()}`,
            updatedAt: new Date().toISOString(),
            verified: false
        };

        setScannedFingers(prev => ({ ...prev, [selectedFinger]: fingerData }));
        Toast.fire({ icon: "success", title: `${selectedFinger} print captured! Click Save to confirm.` });
    };

    const handleSaveBiometrics = async () => {
        if (!foundStudent?.docId || !selectedGuardian) return;

        setLoading(true);
        try {
            const admissionRef = doc(db, "admission", foundStudent.docId);
            const cleanedScannedFingers = {};
            Object.keys(scannedFingers).forEach((key) => {
                cleanedScannedFingers[key] = {
                    ...scannedFingers[key],
                    base64: scannedFingers[key]?.base64 || "" 
                };
            });

            if (selectedGuardian.type === "FATHER") {
                await updateDoc(admissionRef, { father_fingerprints: cleanedScannedFingers });
            } else if (selectedGuardian.type === "MOTHER") {
                await updateDoc(admissionRef, { mother_fingerprints: cleanedScannedFingers });
            } else if (selectedGuardian.type === "ARRAY_GUARDIAN") {
                const currentGuardians = [...(foundStudent.guardians || [])];
                if (currentGuardians[selectedGuardian.arrayIndex]) {
                    currentGuardians[selectedGuardian.arrayIndex].fingerprints = cleanedScannedFingers;
                    await updateDoc(admissionRef, { guardians: currentGuardians });
                }
            }

            if (selectedGuardian) {
                selectedGuardian.fingerprints = cleanedScannedFingers;
            }

            Swal.fire({
                icon: "success", title: "Saved Successfully!",
                text: `Fingerprint data saved for ${selectedGuardian.name}`,
                didOpen: () => { Swal.getContainer().style.zIndex = "9999"; }
            });

        } catch (err) {
            console.error("Firestore Save Error:", err);
            Toast.fire({ icon: "error", title: "Failed to update Firestore" });
        }
        setLoading(false);
    };

    const handleVerifyFingerprint = async () => {
        if (!selectedGuardian) return Toast.fire({ icon: "warning", title: "Please select a Guardian first" });

        const savedFingerData = scannedFingers[selectedFinger];
        const savedTemplate = savedFingerData?.pattern;
        
        if (!savedTemplate) {
            return Toast.fire({
                icon: "warning", title: `No saved pattern found for ${selectedFinger}. Register and save it first.`
            });
        }

        setLoading(true);
        setVerificationResult(null);

        try {
            const res = await fetch('http://127.0.0.1:8000/api/capture', {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ finger_id: "verify" })
            });
            const data = await res.json();

            if (!res.ok || (data.status !== 'success' && !data.success)) {
                setLoading(false);
                return setVerificationResult({
                    success: false, message: `Capture Failed: ${data.message || 'Device capture failed'}`
                });
            }

            const liveXmlData = data.raw_xml || data.data || data.pattern;

            console.log(liveXmlData);
            console.log(savedTemplate);
            

            const matchRes = await fetch('http://127.0.0.1:8000/api/match', {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    live_xml: liveXmlData,
                    saved_template: savedTemplate,
                    finger_id: selectedFinger
                })
            });

            const matchData = await matchRes.json();

            console.log("Matched:" + matchData);
            
            
            if (matchRes.ok && (matchData.matched || matchData.score >= 14000)) {
                setVerificationResult({
                    success: true, message: `MATCH SUCCESSFUL! Fingerprint verified for ${selectedGuardian.name}.`
                });
                Toast.fire({ icon: "success", title: "Fingerprint Verified!" });

                const admissionRef = doc(db, "admission", foundStudent.docId);
                const updatedFingerprints = {
                    ...scannedFingers,
                    [selectedFinger]: {
                        ...scannedFingers[selectedFinger],
                        verified: true,
                        verifiedAt: new Date().toISOString()
                    }
                };

                if (selectedGuardian.type === "FATHER") {
                    await updateDoc(admissionRef, { father_fingerprints: updatedFingerprints });
                } else if (selectedGuardian.type === "MOTHER") {
                    await updateDoc(admissionRef, { mother_fingerprints: updatedFingerprints });
                } else if (selectedGuardian.type === "ARRAY_GUARDIAN") {
                    const currentGuardians = [...(foundStudent.guardians || [])];
                    if (currentGuardians[selectedGuardian.arrayIndex]) {
                        currentGuardians[selectedGuardian.arrayIndex].fingerprints = updatedFingerprints;
                        await updateDoc(admissionRef, { guardians: currentGuardians });
                    }
                }
                
                setScannedFingers(updatedFingerprints);

            } else {
                setVerificationResult({
                    success: false, message: `MATCH FAILED! Print does not match registered ${selectedFinger}.`
                });
                Toast.fire({ icon: "error", title: "Verification Failed" });
            }

        } catch (err) {
            console.error("Verification error:", err);
            setVerificationResult({ success: false, message: "API Error: Unable to communicate with matcher service." });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box p={3} bgcolor="#f4f7f6" minHeight="100vh">
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
                <Box>
                    <Typography variant="h5" fontWeight="bold" color="teal">Student Biometric Portal</Typography>
                    <Typography variant="caption" color="text.secondary">Guardian Fingerprint Registration & Matching</Typography>
                </Box>
                <Button variant="contained" onClick={() => { resetForm(); setOpen(true); }} sx={{ bgcolor: "teal" }} startIcon={<FingerprintIcon />}>
                    Register / Verify Student Guardian
                </Button>
            </Stack>

            <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="md">
                <DialogTitle sx={{ bgcolor: "teal", color: "white", p: 1.5 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="h6">Student Guardian Biometrics</Typography>
                        <IconButton onClick={() => setOpen(false)} sx={{ color: "white" }}><CloseIcon /></IconButton>
                    </Stack>
                </DialogTitle>

                <DialogContent sx={{ p: 3, mt: 1 }}>
                    <Grid className="row" >
                        <Grid className="col-sm-5 mb-3">
                            <Stack spacing={2}>
                                <Typography variant="subtitle2" fontWeight="bold" color="teal">1. Search Student Record</Typography>
                                <Stack direction="row" spacing={1}>
                                    <TextField
                                        label="Guardian Mobile No"
                                        fullWidth size="small" value={mobileNumber}
                                        onChange={(e) => setMobileNumber(e.target.value)}
                                    />
                                    <Button variant="contained" onClick={handleSearchStudent} disabled={searching} sx={{ bgcolor: "teal", minWidth: 48 }}>
                                        {searching ? <CircularProgress size={20} color="inherit" /> : <SearchIcon />}
                                    </Button>
                                </Stack>

                                {foundStudent && (
                                    <Paper variant="outlined" sx={{ p: 1.5, bgcolor: '#e0f2f1', borderColor: 'teal' }}>
                                        <Stack direction="row" spacing={1.5} alignItems="center">
                                            <Avatar src={foundStudent.studentPhoto} sx={{ width: 45, height: 45 }} variant="rounded">
                                                <SchoolIcon />
                                            </Avatar>
                                            <Box>
                                                <Typography variant="subtitle2" fontWeight="bold" color="teal">{foundStudent.studentFullName}</Typography>
                                                <Chip label={`Class: ${foundStudent.resolvedClassName}`} size="small" sx={{ bgcolor: 'teal', color: 'white', height: 20, fontSize: '0.65rem' }} />
                                            </Box>
                                        </Stack>
                                    </Paper>
                                )}

                                {availableGuardians.length > 0 && (
                                    <Box>
                                        <Typography variant="caption" fontWeight="bold" color="text.secondary" display="block" mb={1}>
                                            SELECT TARGET PERSON
                                        </Typography>
                                        <Stack spacing={1} sx={{ maxHeight: 280, overflowY: 'auto', pr: 0.5 }}>
                                            {availableGuardians.map((g, idx) => (
                                                <Card key={idx} variant="outlined" onClick={() => handleSelectGuardian(g)}
                                                    sx={{
                                                        p: 1.5, cursor: 'pointer',
                                                        borderColor: selectedGuardian?.name === g.name && selectedGuardian?.relation === g.relation ? 'teal' : '#e0e0e0',
                                                        bgcolor: selectedGuardian?.name === g.name && selectedGuardian?.relation === g.relation ? '#f0f7f7' : '#fff'
                                                    }}
                                                >
                                                    <Stack direction="row" spacing={1.5} alignItems="center">
                                                        <Avatar src={g.photo}>{g.name[0]}</Avatar>
                                                        <Box flexGrow={1}>
                                                            <Typography variant="body2" fontWeight="bold">{g.name}</Typography>
                                                            <Typography variant="caption" color="text.secondary">{g.relation} • {g.mobile}</Typography>
                                                        </Box>
                                                        <Chip label={`${Object.keys(g.fingerprints || {}).length} Prints`} size="small" color={Object.keys(g.fingerprints || {}).length > 0 ? "success" : "default"} />
                                                    </Stack>
                                                </Card>
                                            ))}
                                        </Stack>
                                    </Box>
                                )}
                            </Stack>
                        </Grid>
                        <Grid className="col-sm-7 mb-3" >
                            <Paper variant="outlined" sx={{ p: 2.5, bgcolor: '#fafafa', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                                <Box>
                                    <Typography variant="subtitle2" fontWeight="bold" color="teal" align="center" mb={1}>
                                        2. HARDWARE CAPTURE & VERIFY
                                    </Typography>

                                    {selectedGuardian && (
                                        <Chip label={`Target: ${selectedGuardian.name} (${selectedGuardian.relation})`} color="primary" variant="outlined" sx={{ width: '100%', mb: 1.5, fontWeight: 'bold' }} />
                                    )}

                                    <Box textAlign="center" my={1.5}>
                                        <Badge badgeContent={loading ? <CircularProgress size={16} /> : null} color="teal">
                                            <Avatar sx={{ width: 70, height: 70, mx: 'auto', bgcolor: loading ? '#e0f2f1' : 'teal' }}>
                                                <FingerprintIcon sx={{ fontSize: 40, color: loading ? 'teal' : 'white' }} />
                                            </Avatar>
                                        </Badge>
                                        <Typography variant="caption" display="block" color="text.secondary" mt={0.5}>
                                            Device Status: Ready (http://127.0.0.1:8000)
                                        </Typography>
                                    </Box>

                                    <TextField select label="Select Finger Target" fullWidth size="small" value={selectedFinger} onChange={(e) => setSelectedFinger(e.target.value)} sx={{ mb: 2 }}>
                                        {FINGERS.map(f => (
                                            <MenuItem key={f.id} value={f.id}>{f.label}</MenuItem>
                                        ))}
                                    </TextField>

                                    {/* Thumbnail Image Display Preview */}
                                    {scannedFingers[selectedFinger]?.base64 && (
                                        <Box textAlign="center" mb={2}>
                                            <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>Captured Thumbnail Preview:</Typography>
                                            <Box 
                                                component="img" 
                                                src={`data:image/bmp;base64,${scannedFingers[selectedFinger].base64}`} 
                                                alt="Fingerprint Scan" 
                                                sx={{ width: 90, height: 110, border: '1px solid #ccc', borderRadius: 1, objectFit: 'contain', bgcolor: '#fff', p: 0.5 }} 
                                            />
                                        </Box>
                                    )}

                                    <Grid container spacing={1.5} mb={2}>
                                        <Grid item xs={6}>
                                            <Button fullWidth variant="contained" onClick={captureFingerprint} disabled={loading || !selectedGuardian} sx={{ bgcolor: "teal" }} startIcon={<FingerprintIcon />}>
                                                Capture
                                            </Button>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Button fullWidth variant="outlined" color="secondary" onClick={handleVerifyFingerprint} disabled={loading || !selectedGuardian} startIcon={<VerifiedIcon />}>
                                                Verify
                                            </Button>
                                        </Grid>
                                    </Grid>

                                    {verificationResult && (
                                        <Alert severity={verificationResult.success ? "success" : "error"} icon={verificationResult.success ? <CheckCircleIcon /> : <ErrorIcon />} sx={{ mt: 1 }}>
                                            {verificationResult.message}
                                        </Alert>
                                    )}
                                </Box>

                                <Box mt={2}>
                                    <Divider sx={{ mb: 1.5 }} />
                                    <Typography variant="caption" fontWeight="bold" color="text.secondary" display="block" mb={1}>
                                        STATUS: SAVED FINGERPRINTS
                                    </Typography>
                                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                        {FINGERS.map(f => {
                                            const fingerObj = scannedFingers[f.id];
                                            const isCaptured = !!fingerObj;
                                            const isVerified = fingerObj?.verified;

                                            return (
                                                <Chip
                                                    key={f.id}
                                                    label={f.label}
                                                    size="small"
                                                    color={isVerified ? "success" : isCaptured ? "primary" : "default"}
                                                    icon={isVerified ? <GppGoodIcon /> : isCaptured ? <CheckCircleIcon /> : <FingerprintIcon />}
                                                    variant={isCaptured ? "filled" : "outlined"}
                                                />
                                            );
                                        })}
                                    </Stack>
                                </Box>
                            </Paper>
                        </Grid>
                    </Grid>

                    <Button fullWidth variant="contained" size="large" sx={{ mt: 3, bgcolor: "teal" }} onClick={handleSaveBiometrics} disabled={loading || !foundStudent || !selectedGuardian}>
                        {loading ? <CircularProgress size={24} color="inherit" /> : `SAVE CAPTURED DATA TO ${selectedGuardian?.name?.toUpperCase() || "GUARDIAN"}`}
                    </Button>
                </DialogContent>
            </Dialog>
        </Box>
    );
}

export default Fingerprint;